import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance, getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { WorkshopFile } from "@/types/workshop.type";
import {
  WORKSHOP_FILE_TYPES_MESSAGE,
  attachmentKindOf,
  isDataFile,
  isPdfFile,
  isVideoFile,
  uploadPickedFile,
  type AttachmentEndpoints,
  type ServerAttachment,
} from "../../api/useSessionAttachments";
import { workshopFilesKey, workshopFilesListKey, workshopThreadFilesKey } from "./workshopKeys";

/** Non-terminal files keep the list fresh; a settled list stops polling. */
const FILE_POLL_MS = 4000;

/**
 * A file as the room lists it. Client-only: the placeholder of an upload from
 * the room carries how many bytes have reached storage, when the upload can
 * say (a PDF or a video goes up as picked; a spreadsheet is converted first,
 * so its progress is not the picked file's and it shows no numbers).
 */
export type WorkshopFileRow = WorkshopFile & {
  upload?: { sent: number; total: number } | null;
};

const filesUrl = (projectId: string | null) => `/workshop/projects/${projectId}/files`;

function isSettled(file: WorkshopFile): boolean {
  return file.state === "completed" || file.state === "failed";
}

/**
 * Every file the project owns (PDFs, videos, spreadsheets, text) — uploaded in
 * any thread or in the Sources tab. Polls only while one is still being read.
 */
export function useWorkshopFiles(projectId: string | null) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: workshopFilesListKey(projectId),
    queryFn: async (): Promise<WorkshopFileRow[]> => {
      const res = await axiosInstance.get<{ files: WorkshopFile[] }>(filesUrl(projectId));
      // A room upload in flight is not on the server yet: keep its row (and
      // its progress) through the poll that runs while it uploads. The upload
      // itself takes it out when it lands or fails.
      const inFlight = (
        qc.getQueryData<WorkshopFileRow[]>(workshopFilesListKey(projectId)) ?? []
      ).filter((f) => f.id.startsWith("pending-") && f.state === "uploading");
      return [...res.data.files, ...inFlight];
    },
    enabled: !!projectId,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((f) => !isSettled(f)) ? FILE_POLL_MS : false,
  });
}

/**
 * The chat strip's endpoints: the same shared attachment hook, pointed at the
 * project, so a file attached in a thread belongs to the project.
 */
export function workshopAttachmentEndpoints(projectId: string): AttachmentEndpoints {
  return {
    queryKey: (sessionId) => workshopThreadFilesKey(projectId, sessionId),
    list: filesUrl(projectId),
    parseList: (data) => (data as { files: ServerAttachment[] }).files,
    uploadUrl: `${filesUrl(projectId)}/upload-url`,
    process: `${filesUrl(projectId)}/process`,
    retry: (documentId) => `${filesUrl(projectId)}/${documentId}/retry`,
    processedId: (data) => (data as { file?: { id?: string } } | null)?.file?.id ?? null,
    alsoInvalidate: [workshopFilesKey(projectId)],
    acceptsDataFiles: true,
  };
}

export type WorkshopFileUrl = {
  url: string;
  expiresAt: string;
  fileName: string | null;
  type: string;
  mimeType: string | null;
};

/** Short-lived signed URL so the Sources tab can show or play the file. */
export function useWorkshopFileUrl(
  projectId: string | null,
  documentId: string | null,
) {
  return useQuery({
    queryKey: [...workshopFilesKey(projectId), "url", documentId],
    queryFn: async () => {
      const res = await axiosInstance.get<WorkshopFileUrl>(
        `/workshop/projects/${projectId}/files/${documentId}/url`,
      );
      return res.data;
    },
    enabled: !!projectId && !!documentId,
    staleTime: 60_000,
  });
}

export function useWorkshopFileMutations(projectId: string | null) {
  const qc = useQueryClient();
  const listKey = workshopFilesListKey(projectId);

  const invalidate = () => qc.invalidateQueries({ queryKey: workshopFilesKey(projectId) });

  const setList = (update: (list: WorkshopFileRow[]) => WorkshopFileRow[]) =>
    qc.setQueryData<WorkshopFileRow[]>(listKey, (list = []) => update(list));

  /**
   * Upload from the room, with no thread. Each file shows as "uploading" straight
   * away and is replaced by the server row once it lands. A PDF or a video
   * reports its bytes as they reach storage, in whole percents.
   */
  const upload = useMutation({
    mutationFn: async (vars: { file: File; previewId: string }) => {
      if (!isPdfFile(vars.file) && !isVideoFile(vars.file) && !isDataFile(vars.file)) {
        throw new Error(WORKSHOP_FILE_TYPES_MESSAGE);
      }
      const total = vars.file.size;
      let lastPct = -1;
      const onProgress = isDataFile(vars.file)
        ? undefined
        : (fraction: number) => {
            const pct = Math.floor(fraction * 100);
            if (pct === lastPct) return;
            lastPct = pct;
            setList((list) =>
              list.map((f) =>
                f.id === vars.previewId
                  ? { ...f, upload: { sent: Math.round((pct / 100) * total), total } }
                  : f,
              ),
            );
          };
      await uploadPickedFile(
        vars.file,
        {
          uploadUrl: `${filesUrl(projectId)}/upload-url`,
          process: `${filesUrl(projectId)}/process`,
          sessionId: null,
        },
        onProgress,
      );
    },
    onMutate: async ({ file, previewId }) => {
      await qc.cancelQueries({ queryKey: listKey });
      setList((list) => [
        ...list,
        {
          id: previewId,
          fileName: file.name,
          type: attachmentKindOf(file),
          state: "uploading",
          createdAt: new Date().toISOString(),
          durationSec: null,
          error: null,
          canRetry: false,
          sessionId: null,
          upload: isDataFile(file) ? null : { sent: 0, total: file.size },
        },
      ]);
    },
    onError: (error, { file, previewId }) => {
      setList((list) => list.filter((f) => f.id !== previewId));
      // A workbook that failed on a later sheet has already uploaded the earlier ones.
      void invalidate();
      queue.add({
        title: `Couldn't upload ${file.name}`,
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    // The server row replaces the placeholder on the refetch.
    onSuccess: async (_data, { previewId }) => {
      await invalidate();
      setList((list) => list.filter((f) => f.id !== previewId));
    },
  });

  /** Removes the file from the project (its row). The stored file stays. */
  const remove = useMutation({
    mutationKey: ["workshopFileRemove", projectId],
    mutationFn: async (documentId: string) => {
      await axiosInstance.delete(`${filesUrl(projectId)}/${documentId}`);
    },
    onMutate: async (documentId) => {
      await qc.cancelQueries({ queryKey: workshopFilesKey(projectId) });
      const list = qc.getQueryData<WorkshopFile[]>(listKey) ?? [];
      const index = list.findIndex((f) => f.id === documentId);
      setList((current) => current.filter((f) => f.id !== documentId));
      return { row: index >= 0 ? list[index] : null, index };
    },
    onError: (error, _documentId, context) => {
      const row = context?.row;
      if (row) {
        setList((list) => {
          if (list.some((f) => f.id === row.id)) return list;
          const next = [...list];
          next.splice(Math.min(context.index, next.length), 0, row);
          return next;
        });
      }
      queue.add({
        title: "Couldn't remove the file",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSettled: () => {
      if (qc.isMutating({ mutationKey: ["workshopFileRemove", projectId] }) > 1) return;
      void invalidate();
    },
  });

  const retry = useMutation({
    mutationFn: async (documentId: string) => {
      await axiosInstance.post(`${filesUrl(projectId)}/${documentId}/retry`);
    },
    onMutate: async (documentId) => {
      await qc.cancelQueries({ queryKey: listKey });
      setList((list) =>
        list.map((f) =>
          f.id === documentId ? { ...f, state: "processing", error: null, canRetry: false } : f,
        ),
      );
    },
    onError: (error) => {
      queue.add({
        title: "Couldn't retry the file",
        description: getAiRequestErrorMessage(error),
        variant: "warning",
      });
    },
    onSettled: () => void invalidate(),
  });

  return { upload, remove, retry };
}
